import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollText, ExternalLink, Loader2, CheckCircle2 } from "lucide-react";
import { openDocumento } from "@/pages/Documenti";
import { toast } from "sonner";

export default function AckGate() {
  const qc = useQueryClient();
  const [opened, setOpened] = useState(false);
  const [busy, setBusy] = useState(false);

  const { data: pending = [] } = useQuery({
    queryKey: ["documenti", "pending-ack"],
    queryFn: async () => (await api.get("/documenti/pending-ack")).data,
  });

  if (!pending.length) return null;
  const doc = pending[0];

  const apri = () => {
    openDocumento(doc.id);
    setOpened(true);
  };

  const firma = async () => {
    setBusy(true);
    try {
      await api.post(`/documenti/${doc.id}/ack`);
      setOpened(false);
      await qc.invalidateQueries({ queryKey: ["documenti"] });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="max-w-lg [&>button]:hidden" data-testid="ack-gate">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><ScrollText className="h-5 w-5 text-[#D97706]" /> Regolamento e disposizioni operative</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-slate-600">
            È richiesta la lettura del seguente documento. Ti preghiamo di <strong>leggerlo attentamente</strong> e di
            firmare la presa visione al termine. Dopo la firma non ti verrà più richiesto.
          </p>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="font-semibold text-slate-900">{doc.titolo}</p>
            {doc.descrizione && <p className="mt-1 text-sm text-slate-500">{doc.descrizione}</p>}
            <p className="mt-1 text-xs text-slate-400">{doc.filename}</p>
            {pending.length > 1 && <p className="mt-2 text-xs font-medium text-[#D97706]">Documenti da leggere: {pending.length}</p>}
          </div>
          <Button variant="outline" className="w-full" data-testid="ack-open" onClick={apri}>
            <ExternalLink className="mr-2 h-4 w-4" /> Apri e leggi il documento
          </Button>
          <Button className="w-full bg-[#14382C] hover:bg-[#0E281F]" disabled={!opened || busy} data-testid="ack-sign" onClick={firma}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
            Dichiaro di aver letto — Firma presa visione
          </Button>
          {!opened && <p className="text-center text-xs text-slate-400">Apri prima il documento per poter firmare.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
